/**
 * TypeScript view of the `slitherlink` format.
 * Source of truth: `shared/schema/slitherlink.{definition,submission,solution,progress}.json`.
 *
 * A grid of `rows` × `cols` cells has `(rows + 1) × (cols + 1)` dots. `h[r][c]` joins dot
 * (r, c) to (r, c + 1); `v[r][c]` joins dot (r, c) to (r + 1, c).
 */

export type Localized = Record<string, string | undefined>;

export interface SlitherlinkDefinition {
  puzzleType: "slitherlink";
  typeVersion: number;
  rows: number;
  cols: number;
  /** One string per row of cells: a digit 0-3, or `.` for a cell without a clue. */
  clues: string[];
  rulesText?: Localized;
  meta?: Record<string, unknown>;
}

export const LINE = "#";
export const NONE = ".";
/** A solving aid: the player is sure the edge stays empty. Never sent to the server. */
export const CROSSED = "x";
export const NO_CLUE = ".";
export type EdgeState = typeof LINE | typeof NONE | typeof CROSSED;

export interface Edges {
  /** `rows + 1` strings of `cols` characters. */
  h: string[];
  /** `rows` strings of `cols + 1` characters. */
  v: string[];
}

/** Autosave shape. Opaque to the core; this type owns it and its migrations. */
export interface SlitherlinkProgress extends Edges {
  version: 1;
}

/** `#` a segment and `.` none: crosses are folded into `.`. */
export type SlitherlinkSubmission = Edges;

/** The author's loop, in the submission format. */
export type SlitherlinkSolution = Edges;

export const MAX_SIZE = 30;

export type Axis = "h" | "v";
export interface Edge {
  axis: Axis;
  r: number;
  c: number;
}
export type Dot = [row: number, col: number];
export type Cell = [row: number, col: number];

export function edgeKey(edge: Edge): string {
  return `${edge.axis}:${edge.r}:${edge.c}`;
}

export function dotKey([r, c]: Dot): string {
  return `${r},${c}`;
}

export function cellId(row: number, col: number): string {
  return `r${row}c${col}`;
}

/** Picks the viewer's language, falling back to the base language, then English, then any. */
export function localized(value: Localized | null | undefined, locale: string): string {
  if (!value) return "";
  return (
    value[locale] ??
    value[locale.split("-")[0]] ??
    value.en ??
    Object.values(value).find((v) => typeof v === "string" && v.length > 0) ??
    ""
  );
}
